import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const brl = (n) => (Number(n || 0)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function ItensTab() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [unidade, setUnidade] = useState("un");
  const [valor, setValor] = useState("0");

  const load = async () => {
    try { const { data } = await api.get("/itens"); setList(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setNome(""); setDescricao(""); setUnidade("un"); setValor("0"); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it); setNome(it.nome); setDescricao(it.descricao || "");
    setUnidade(it.unidade || "un"); setValor(String(it.valor ?? 0)); setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { nome, descricao, unidade, valor: Number(valor) || 0 };
      if (editing) { await api.put(`/itens/${editing.id}`, payload); toast.success("Item atualizado"); }
      else {
        if (!nome) { toast.error("Informe o nome"); return; }
        await api.post("/itens", payload); toast.success("Item criado");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este item?")) return;
    try { await api.delete(`/itens/${id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-heading">Itens</CardTitle>
          <CardDescription>Cadastre os itens e o valor unitário de cada um.</CardDescription>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-item-btn" onClick={openNew} className="btn-brand rounded-full">
              <Plus className="h-4 w-4 mr-2" /> Novo item
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Editar item" : "Novo item"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Nome</Label>
                <Input data-testid="item-nome-input" value={nome} onChange={(e) => setNome(e.target.value)} /></div>
              <div className="space-y-2"><Label>Descrição</Label>
                <Input data-testid="item-descricao-input" value={descricao} onChange={(e) => setDescricao(e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Unidade (un, kg, l...)</Label>
                  <Input data-testid="item-unidade-input" value={unidade} onChange={(e) => setUnidade(e.target.value)} /></div>
                <div className="space-y-2"><Label>Valor unitário (R$)</Label>
                  <Input data-testid="item-valor-input" type="number" min="0" step="0.01" value={valor} onChange={(e) => setValor(e.target.value)} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} data-testid="item-save-btn" className="btn-brand">Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Nome</TableHead><TableHead>Descrição</TableHead>
              <TableHead>Unidade</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead className="w-32">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">Nenhum item cadastrado.</TableCell></TableRow>}
              {list.map((it, i) => (
                <TableRow key={it.id} className="row-fade" style={{ animationDelay: `${i * 30}ms` }}>
                  <TableCell className="font-medium">{it.nome}</TableCell>
                  <TableCell className="text-slate-600">{it.descricao}</TableCell>
                  <TableCell className="text-slate-600">{it.unidade}</TableCell>
                  <TableCell className="text-right font-mono">{brl(it.valor)}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(it)} data-testid={`edit-item-${it.id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => remove(it.id)} data-testid={`del-item-${it.id}`}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
